import { PatientNoteSessionSummarySection } from '~/components/maud/patients/note/PatientNoteSessionSummarySection';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import { coalesceNoteContentToHtml, EditorV2 } from '@equip.health/ui';
import PatientNoteTemplateRevisionForm from '~/components/maud/patients/note/PatientNoteTemplateRevisionForm';
import { PatientNoteTemplateSwitchModal } from '~/components/maud/patients/note/PatientNoteTemplateSwitchModal';
import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useFormikContext } from 'formik';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PatientNoteCarePlanSection } from '~/components/maud/patients/note/PatientNoteCarePlanSection';
import { ErrorBoundary } from 'react-error-boundary';
import { PatientNoteCarePlanSectionErrorFallback } from '~/components/maud/patients/note/PatientNoteCarePlanSectionErrorFallback';

type Props = {
  onFormChange: () => void;
  clickedTemplateId: string | null;
  isTemplateSwitching: boolean;
  setClickedTemplateId: Dispatch<SetStateAction<string | null>>;
  setIsTemplateSwitching: Dispatch<SetStateAction<boolean>>;
};

export const EditPatientNoteFormContentSection = ({
  onFormChange,
  clickedTemplateId,
  isTemplateSwitching,
  setClickedTemplateId,
  setIsTemplateSwitching,
}: Props) => {
  const {
    carePlanNotesDisplay: isCarePlanNotesDisplayEnabled,
    carePlanM4MedPsychNotes: isCarePlanM4MedPsychNotesEnabled,
  } = useFlags<LDFlags>();
  const { values: formValues } = useFormikContext<EditNoteFormData>();
  const { fieldErrors, setHasUnsavedChanges, noteContentEditorRef } =
    useEditPatientNoteFormContext();
  const { noteTemplateRevisions } =
    useLatestNoteTemplateRevisionsWithLegacyHandling(
      formValues.noteTemplateRevision?.node,
    );

  const selectedNoteTemplate = noteTemplateRevisions?.find(
    (rev) => rev.templateRevisionId === formValues.noteTemplateRevision?.node,
  )?.template;

  const htmlNoteContent: string | null = formValues.noteContentCache
    ? coalesceNoteContentToHtml(formValues.noteContentCache)
    : null;

  const isMedPsychTemplate =
    selectedNoteTemplate?.templateCode === 'FUP_MED_TEAM' ||
    selectedNoteTemplate?.templateCode === 'INTAKE_MED_TEAM';

  // Med/psych templates gate on the LD flag alone: the care plan DB flags are
  // not yet populated for these templates in the target environments, so the
  // flag is the rollout gate. Non-med templates keep the DB-flag rule.
  const shouldShowCarePlanSection = isMedPsychTemplate
    ? isCarePlanM4MedPsychNotesEnabled
    : isCarePlanNotesDisplayEnabled && !!selectedNoteTemplate?.showCarePlan;

  useEffect(() => {
    noteContentEditorRef.current?.chain().setContent(htmlNoteContent).run();
  }, [htmlNoteContent]);

  return (
    <>
      {selectedNoteTemplate?.showSessionSummary && (
        <PatientNoteSessionSummarySection noteTemplate={selectedNoteTemplate} />
      )}

      {shouldShowCarePlanSection && (
        // remounts the boundary and section on template switch, so the care plan
        // loads, or retries after an error, for the selected template
        <ErrorBoundary
          key={formValues.noteTemplateRevision?.node}
          fallback={<PatientNoteCarePlanSectionErrorFallback />}
        >
          <PatientNoteCarePlanSection />
        </ErrorBoundary>
      )}
      {(!formValues.noteTemplateRevision ||
        formValues.noteTemplateRevision?.node ===
          NO_NOTE_TEMPLATE_OPTION.node) && (
        <EditorV2
          initialContent={htmlNoteContent}
          isInvalid={Boolean(fieldErrors.editorError)}
          minHeight="100%"
          onChange={() => {
            setHasUnsavedChanges(true);
            onFormChange();
          }}
          stateRef={noteContentEditorRef}
        />
      )}

      {formValues &&
        formValues.noteTemplateRevision?.node !==
          NO_NOTE_TEMPLATE_OPTION.node && <PatientNoteTemplateRevisionForm />}

      <PatientNoteTemplateSwitchModal
        clickedTemplateId={clickedTemplateId}
        closeModal={() => {
          setClickedTemplateId(null);
          setIsTemplateSwitching(false);
        }}
        isOpen={isTemplateSwitching}
        onCancel={() => {
          setIsTemplateSwitching(false);
          setClickedTemplateId(formValues.noteTemplateRevision?.node);
        }}
      />
    </>
  );
};
